import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { SubmitHandler, useForm } from "react-hook-form";
import TextareaField from "../../../components/common/TextareaField";
import toast from "react-hot-toast";
import { useOrderReverseMutation } from "../../../apis/orderApis";
import { useLanguage } from "../../../context/LanguageContext";

// Define the expected form data type
interface ReverseOrderFormData {
  awbNumber: string;
}

export default function ReverseOrderModal({
  onClose,
  fetchData,
  isSidebar = false,
}: {
  onClose: () => void;
  fetchData?: () => void;
  isSidebar?: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ReverseOrderFormData>({
    defaultValues: {
      awbNumber: "",
    },
  });

  const { mutate: reverseOrderMutate, isPending: isReverseOrderPending } = useOrderReverseMutation();
  const { t } = useLanguage();

  const onSubmit: SubmitHandler<ReverseOrderFormData> = async (data: any) => {
    const awbArray = data.awbNumber
      .split("\n")
      .map((v: any) => v.trim())
      .filter((v: any) => v.length > 0);

    reverseOrderMutate(
      { orderId: awbArray },
      {
        onSuccess: (data: any) => {
          onClose();
          toast.success(t('Reverse Created Successfully'))
          fetchData?.()
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(t(error?.message || error || 'Failed to create reverse order'));
        },
      }
    );

  };

  const content = (
    <form
      className="flex flex-col w-full h-full font-sans bg-transparent"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex items-center flex-shrink-0">
        <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] px-6 py-4 w-full rounded-tl-3xl flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#212121]">
            {t("Create Reverse Order")}
          </h2>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 mr-4 flex items-center justify-center flex-shrink-0 hover:bg-gray-300 transition"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="w-full py-6 px-8 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-1 overflow-y-auto min-h-0">
        <div className="w-full space-y-4">
          <TextareaField
            name="awbNumber"
            rows={8}
            label={t("Enter AWB Numbers (one per line)")}
            placeholder={t("Enter AWB Number 1") + "\n" + t("Enter AWB Number 2") + "\n..."}
            register={register}
            validation={{
              required: t("AWB number(s) are required."),
              pattern: {
                value: /^[a-zA-Z0-9\s]+$/,
                message: t("Only alphanumeric characters and new lines are allowed.")
              }
            }}
            error={errors.awbNumber}
          />
        </div>
      </div>

      <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end gap-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-shrink-0">
        <button
          type="submit"
          disabled={isReverseOrderPending}
          className="w-full sm:w-auto min-w-[140px] h-11 px-6 rounded-full font-semibold transition-all duration-200 flex items-center justify-center gap-2 bg-[#FBD037] hover:bg-[#f7c400] text-black shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isReverseOrderPending ? t("Submitting...") : t("Submit")}
        </button>
      </div>
    </form>
  );

  if (isSidebar) {
    return content;
  }

  return createPortal(
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4 font-sans">
      <div className="rounded-2xl w-full max-w-[500px] h-[calc(100vh-2rem)] flex flex-col relative bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-right duration-300">
        {content}
      </div>
    </div>,
    document.body
  );
}